import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "~/ui/dialog"
import { Button } from "~/ui/button"
import { gql } from "~/__generated__"
import { useSafeMutation } from "~/common/use-safe-mutation"

const DECLINE_PLAN_MUTATION = gql(/* GraphQL */ `
  mutation PlanParticipationDecline($planId: ID!) {
    planParticipationDecline(input: { planId: $planId }) {
      success
      plan {
        id
        title
        description
        datetime
        minPeople
        maxPeople
        planParticipations {
          edges {
            node {
              id
              guestsCount
              user {
                id
              }
            }
          }
        }
      }
    }
  }
`)

const JOIN_PLAN_MUTATION = gql(/* GraphQL */ `
  mutation PlanParticipationCreate($planId: ID!) {
    planParticipationCreate(input: { planId: $planId }) {
      planParticipation {
        id
        canJoinAt
        guestsCount
        user {
          id
        }
      }
      plan {
        id
        title
        description
        datetime
        minPeople
        maxPeople
        planParticipations {
          edges {
            node {
              id
              guestsCount
              user {
                id
              }
            }
          }
        }
      }
    }
  }
`)

interface JoinPlanModalProps {
  planId: string
  planTitle: string
  isOpen: boolean
  onClose: () => void
  isJoined?: boolean
}

export const JoinPlanModal = ({
  planId,
  planTitle,
  isOpen,
  onClose,
  isJoined = false,
}: JoinPlanModalProps) => {
  const [joinPlan, { loading: joinLoading }] = useSafeMutation(JOIN_PLAN_MUTATION, {
    update(cache, { data }) {
      if (data?.planParticipationCreate?.plan) {
        cache.modify({
          id: cache.identify({ __typename: "Plan", id: planId }),
          fields: {
            planParticipations() {
              return data.planParticipationCreate.plan.planParticipations
            },
          },
        })
      }
    },
  })
  const [declinePlan, { loading: declineLoading }] = useSafeMutation(DECLINE_PLAN_MUTATION, {
    update(cache, { data }) {
      if (data?.planParticipationDecline?.plan) {
        cache.modify({
          id: cache.identify({ __typename: "Plan", id: planId }),
          fields: {
            planParticipations() {
              return data.planParticipationDecline.plan.planParticipations
            },
          },
        })
      }
    },
  })

  const handleJoin = async () => {
    const { data, errors } = await joinPlan({
      variables: {
        planId,
      },
    })

    if (data && !errors) {
      onClose()
      // You might want to show a success toast here
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {isJoined ? "Leave" : "Join"} "{planTitle}"
          </DialogTitle>
          <DialogDescription>
            {isJoined
              ? "Are you sure you want to leave this plan?"
              : "Are you sure you want to join this plan?"}
          </DialogDescription>
        </DialogHeader>
        <div className="mt-4 flex justify-end gap-4">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          {isJoined ? (
            <Button
              variant="destructive"
              onClick={async () => {
                const { data, errors } = await declinePlan({
                  variables: {
                    planId,
                  },
                })
                if (data && !errors) {
                  onClose()
                }
              }}
              disabled={declineLoading}
            >
              Yes, leave plan
            </Button>
          ) : (
            <Button onClick={handleJoin} disabled={joinLoading}>
              Yes, I'm in!
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
